import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ORCHID_LOGO } from "@/lib/brand";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/dashboard");
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Sign in failed. Please check your credentials.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left — branding */}
            <div className="hidden lg:flex relative overflow-hidden bg-orchid-950 text-ivory flex-col justify-between p-12">
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage:
                            'url("https://images.unsplash.com/photo-1724947052687-e580b3010aad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBoZXJpdGFnZSUyMGx1eHVyeSUyMHJlc29ydHxlbnwwfHx8fDE3Nzk4MDI4ODN8MA&ixlib=rb-4.1.0&q=85")',
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        filter: "grayscale(30%) contrast(0.9)",
                    }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(135deg, rgba(2,44,34,0.92) 0%, rgba(6,78,59,0.85) 50%, rgba(2,44,34,0.95) 100%)",
                    }}
                />
                <div className="relative">
                    <div className="bg-white/95 rounded-lg p-4 inline-block shadow-lift">
                        <img src={ORCHID_LOGO} alt="The Orchid Hotels" className="h-16 w-auto" />
                    </div>
                </div>
                <div className="relative max-w-md">
                    <h2 className="font-heading text-5xl leading-tight mb-6">
                        Every guest <em className="text-gold-500 not-italic">deserves</em> a warm welcome.
                    </h2>
                    <p className="text-stone-300 leading-relaxed">
                        Manage every conversation, every lead, every Petal — all in one elegant
                        console designed for The Orchid Hotels.
                    </p>
                </div>
                <div className="relative text-xs uppercase tracking-[0.3em] text-stone-400">
                    Mumbai · Pune · Shimla · Manali · Goa · Lonavala · Rishikesh
                </div>
            </div>

            {/* Right — form */}
            <div className="flex items-center justify-center p-8 bg-ivory">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-8 flex items-center">
                        <img src={ORCHID_LOGO} alt="The Orchid Hotels" className="h-12 w-auto" />
                    </div>

                    <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">
                        Admin Console
                    </div>
                    <h1 className="font-heading text-5xl text-orchid-700 mb-2">Welcome back.</h1>
                    <p className="text-stone-600 mb-10">
                        Sign in to manage conversations, leads, and the knowledge base.
                    </p>

                    <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">
                                Email
                            </label>
                            <div className="relative">
                                <Mail
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
                                />
                                <input
                                    data-testid="login-email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-4 py-3 rounded-md border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-orchid-700/15 focus:border-orchid-700 text-stone-900"
                                    placeholder="admin@orchidhotel.com"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">
                                Password
                            </label>
                            <div className="relative">
                                <Lock
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
                                />
                                <input
                                    data-testid="login-password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-4 py-3 rounded-md border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-orchid-700/15 focus:border-orchid-700 text-stone-900"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        {error && (
                            <div
                                data-testid="login-error"
                                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            data-testid="login-submit"
                            type="submit"
                            disabled={loading}
                            className="w-full flex items-center justify-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white font-medium px-6 py-3 rounded-md transition-colors disabled:opacity-60"
                        >
                            {loading ? "Signing in…" : (
                                <>
                                    Sign in <ArrowRight size={16} />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-12 text-xs text-stone-500">
                        © {new Date().getFullYear()} The Orchid Hotels. All rights reserved.
                    </div>
                </div>
            </div>
        </div>
    );
}
